import React, {useMemo} from "react";
import {css} from "@panda/css";

import {Badge} from "@pg-design/badge-module";
import {InfoIcon, SpecialPriceIcon} from "@pg-design/icons-module";
import {Image} from "@pg-design/image-module";
import {Text} from "@pg-design/text-module";
import {useIsMounted} from "@pg-mono/hooks";
import {SimpleSlider} from "@pg-mono/simple-slider";
import {OFFER_TEST_IDS} from "@pg-mono/test-ids";
import {useUserDevice} from "@pg-mono/user-device";

import {offerBoxImgStyle} from "../../../atoms/OfferBoxBase";
import {useResponsiveOpenLink} from "../../../hooks/use_responsive_open_link";
import {IOfferBoxOffer} from "../../types/IOfferBoxOffer";
import {hasOfferActivePromotions} from "../../utils/has_offer_active_promotions";
import {hasOfferNoContributionPromotion} from "../../utils/has_offer_no_contribution_promotion";
import {isOfferPreSale} from "../../utils/is_offer_pre_sale";
import {OfferBoxTopSectionMap} from "./OfferBoxTopSectionMap";

const presaleOfferDefaultImg = require("../../images/presale_offer_default_img_375x212.jpg");

interface IProps {
    offer: IOfferBoxOffer;
    offerLink: string;
    index?: number;
    fetchPriority?: "high" | "low" | "auto";
    onInfoPanelToggleClick: (event: React.MouseEvent) => void;
    onSlideClick?: () => void;
    onPrevClick?: (slideIndex: number) => void;
    onNextClick?: (slideIndex: number) => void;
}

export const OfferBoxTopSectionSlider = (props: IProps) => {
    const {fetchPriority, offer, onInfoPanelToggleClick} = props;
    const {isMobile} = useUserDevice();
    const mainImageMobile = offer.main_image?.m_img_375x211;
    const mainImageDesktop = offer.main_image && "m_img_500" in offer.main_image ? offer.main_image?.m_img_500 : offer.main_image?.m_img_750;
    const mainImage = (isMobile ? mainImageMobile : mainImageDesktop) || (isOfferPreSale(offer) ? presaleOfferDefaultImg : undefined);
    const mapImage = "map_image" in offer ? offer.map_image : undefined;
    const gallery = useMemo(() => {
        return "gallery" in offer && offer.gallery ? offer.gallery.map((record) => record?.image).filter(Boolean) : [];
    }, [offer.id]);
    const isMounted = useIsMounted();

    const openResponsiveOfferLink = useResponsiveOpenLink(props.offerLink);
    const handleSlideClick = () => {
        props.onSlideClick?.();
        openResponsiveOfferLink();
    };

    return (
        <div className={offerBoxTopSectionStyle} title={offer.name}>
            <SimpleSlider
                leftArrow={({currentSlide}) => currentSlide > 1 && <SimpleSlider.ArrowLeft />}
                rightArrow={({currentSlide, totalSlides}) => currentSlide < totalSlides && <SimpleSlider.ArrowRight />}
                onPrevClick={props.onPrevClick}
                onNextClick={props.onNextClick}
                onSlideClick={handleSlideClick}
                lazyLoadOffset={1}
                className={sliderStyle}
            >
                <div className={slideStyle}>
                    <Image
                        src={mainImage}
                        alt={offer.name}
                        className={slideImgWrapperStyle}
                        imageStyle={offerBoxImgStyle}
                        width="375px"
                        height={isMobile ? "211px" : "246px"}
                        loading={props.index === 0 ? undefined : "lazy"}
                        fetchPriority={fetchPriority}
                        onClick={handleSlideClick}
                    />

                    {getOfferBoxBadge(props.offer)}

                    {offer.description && (
                        <span onTouchEnd={(e) => e.stopPropagation()}>
                            <InfoIcon
                                onClick={onInfoPanelToggleClick}
                                className={infoIconStyle}
                                size="1.6"
                                wrapperType="circle"
                                wrapperColor="white"
                                wrapperSize="2.2"
                                dataTestid={OFFER_TEST_IDS.LISTING_OFFER_BOX.DESCRIPTION_ICON}
                            />
                        </span>
                    )}
                </div>

                {mapImage ? (
                    <div className={slideStyle}>
                        {isMounted && (
                            <OfferBoxTopSectionMap
                                src={isMobile ? mapImage?.m_img_375x211 : mapImage?.m_img_375x256}
                                src2x={isMobile ? mapImage?.m_img_760x428 : mapImage?.m_img_760x520}
                                src3x={isMobile ? mapImage?.m_img_1125x633 : mapImage?.m_img_1125x769}
                                alt={`${offer.name} - mapa`}
                                className={slideImgWrapperStyle}
                                imageStyle={offerBoxImgStyle}
                                onClick={handleSlideClick}
                            />
                        )}
                    </div>
                ) : (
                    [] // empty array instead of null for React.Children.count to work correctly
                )}

                {gallery.map((galleryImg, index) => (
                    <div key={galleryImg?.g_img_372x210} className={slideStyle}>
                        <Image
                            src={isMobile ? galleryImg?.g_img_372x210 : galleryImg?.g_img_760x428}
                            src2x={galleryImg?.g_img_760x428}
                            src3x={galleryImg?.g_img_1500}
                            alt={`${offer.name} - zdjęcie ${index + 1}`}
                            className={slideImgWrapperStyle}
                            imageStyle={offerBoxImgStyle}
                            width="375px"
                            height={isMobile ? "211px" : "246px"}
                            loading="lazy"
                            fetchPriority="low"
                            onClick={handleSlideClick}
                        />
                    </div>
                ))}
            </SimpleSlider>
        </div>
    );
};

const getOfferBoxBadge = (offer: IOfferBoxOffer) => {
    if (hasOfferNoContributionPromotion(offer)) {
        return (
            <Badge className={topBadgeStyle} variant="label_highlight">
                <Text as="span" variant="info_txt_2" className={specialPriceWrapperStyle}>
                    <SpecialPriceIcon className={specialPriceIconWrapperStyle} size="1.4" />
                    <strong>Bez wkładu własnego</strong>
                </Text>
            </Badge>
        );
    }

    if (hasOfferActivePromotions(offer)) {
        return (
            <Badge className={topBadgeStyle} variant="label_info">
                <Text as="span" variant="info_txt_2">
                    % <strong>Promocje</strong>
                </Text>
            </Badge>
        );
    }

    if (isOfferPreSale(offer)) {
        return (
            <Badge className={topBadgeStyle} variant="label_info">
                <Text as="span" variant="info_txt_2">
                    <strong>Przedsprzedaż</strong>
                </Text>
            </Badge>
        );
    }

    return null;
};

const offerBoxTopSectionStyle = css({
    display: "block",
    position: "relative",
    _hover: {
        color: "inherit",
        textDecoration: "none"
    },
    _focus: {
        color: "inherit",
        textDecoration: "none"
    }
});

const topBadgeStyle = css({
    position: "absolute",
    top: 1.5,
    left: 1.5
});

const specialPriceWrapperStyle = css({
    display: "flex",
    alignItems: "center",
    gap: 1
});

const specialPriceIconWrapperStyle = css({
    width: "1.2rem",
    height: "1.2rem"
});

const infoIconStyle = css({
    position: "absolute",
    bottom: 1.5,
    right: 1.5,
    zIndex: 5,
    cursor: "pointer"
});

const sliderStyle = css({
    overflow: "hidden",
    borderTopLeftRadius: 2,
    borderTopRightRadius: 2,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0
});

const slideStyle = css({
    position: "relative",
    height: "20.4rem",
    md: {
        height: "24.6rem"
    }
});

const slideImgWrapperStyle = css({
    display: "block",
    cursor: "pointer",
    objectFit: "cover",
    height: "100%",
    width: "100%"
});
import React, {useMemo} from "react";
import {css} from "@panda/css";

import {Badge} from "@pg-design/badge-module";
import {InfoIcon, SpecialPriceIcon} from "@pg-design/icons-module";
import {Image} from "@pg-design/image-module";
import {Text} from "@pg-design/text-module";
import {useIsMounted} from "@pg-mono/hooks";
import {SimpleSlider} from "@pg-mono/simple-slider";
import {OFFER_TEST_IDS} from "@pg-mono/test-ids";
import {useUserDevice} from "@pg-mono/user-device";

import {offerBoxImgStyle} from "../../../atoms/OfferBoxBase";
import {useResponsiveOpenLink} from "../../../hooks/use_responsive_open_link";
import {IOfferBoxOffer} from "../../types/IOfferBoxOffer";
import {hasOfferActivePromotions} from "../../utils/has_offer_active_promotions";
import {hasOfferNoContributionPromotion} from "../../utils/has_offer_no_contribution_promotion";
import {isOfferPreSale} from "../../utils/is_offer_pre_sale";
import {OfferBoxTopSectionMap} from "./OfferBoxTopSectionMap";

const presaleOfferDefaultImg = require("../../images/presale_offer_default_img_375x212.jpg");

interface IProps {
    offer: IOfferBoxOffer;
    offerLink: string;
    index?: number;
    fetchPriority?: "high" | "low" | "auto";
    onInfoPanelToggleClick: (event: React.MouseEvent) => void;
    onSlideClick?: () => void;
    onPrevClick?: (slideIndex: number) => void;
    onNextClick?: (slideIndex: number) => void;
}

export const OfferBoxTopSectionSlider = (props: IProps) => {
    const {fetchPriority, offer, onInfoPanelToggleClick} = props;
    const {isMobile} = useUserDevice();
    const mainImageMobile = offer.main_image?.m_img_375x211;
    const mainImageDesktop = offer.main_image && "m_img_500" in offer.main_image ? offer.main_image?.m_img_500 : offer.main_image?.m_img_750;
    const mainImage = (isMobile ? mainImageMobile : mainImageDesktop) || (isOfferPreSale(offer) ? presaleOfferDefaultImg : undefined);
    const mapImage = "map_image" in offer ? offer.map_image : undefined;
    const gallery = useMemo(() => {
        return "gallery" in offer && offer.gallery ? offer.gallery.map((record) => record?.image).filter(Boolean) : [];
    }, [offer.id]);
    const isMounted = useIsMounted();

    const openResponsiveOfferLink = useResponsiveOpenLink(props.offerLink);
    const handleSlideClick = () => {
        props.onSlideClick?.();
        openResponsiveOfferLink();
    };

    return (
        <div className={offerBoxTopSectionStyle} title={offer.name}>
            <SimpleSlider
                leftArrow={({currentSlide}) => currentSlide > 1 && <SimpleSlider.ArrowLeft />}
                rightArrow={({currentSlide, totalSlides}) => currentSlide < totalSlides && <SimpleSlider.ArrowRight />}
                onPrevClick={props.onPrevClick}
                onNextClick={props.onNextClick}
                onSlideClick={handleSlideClick}
                lazyLoadOffset={1}
                className={sliderStyle}
            >
                <div className={slideStyle}>
                    <Image
                        src={mainImage}
                        alt={offer.name}
                        className={slideImgWrapperStyle}
                        imageStyle={offerBoxImgStyle}
                        width="375px"
                        height={isMobile ? "211px" : "246px"}
                        loading={props.index === 0 ? undefined : "lazy"}
                        fetchPriority={fetchPriority}
                        onClick={handleSlideClick}
                    />

                    {getOfferBoxBadge(props.offer)}

                    {offer.description && (
                        <span onTouchEnd={(e) => e.stopPropagation()}>
                            <InfoIcon
                                onClick={onInfoPanelToggleClick}
                                className={infoIconStyle}
                                size="1.6"
                                wrapperType="circle"
                                wrapperColor="white"
                                wrapperSize="2.2"
                                dataTestid={OFFER_TEST_IDS.LISTING_OFFER_BOX.DESCRIPTION_ICON}
                            />
                        </span>
                    )}
                </div>

                {mapImage ? (
                    <div className={slideStyle}>
                        {isMounted && (
                            <OfferBoxTopSectionMap
                                src={isMobile ? mapImage?.m_img_375x211 : mapImage?.m_img_375x256}
                                src2x={isMobile ? mapImage?.m_img_760x428 : mapImage?.m_img_760x520}
                                src3x={isMobile ? mapImage?.m_img_1125x633 : mapImage?.m_img_1125x769}
                                alt={`${offer.name} - mapa`}
                                className={slideImgWrapperStyle}
                                imageStyle={offerBoxImgStyle}
                                onClick={handleSlideClick}
                            />
                        )}
                    </div>
                ) : (
                    [] // empty array instead of null for React.Children.count to work correctly
                )}

                {gallery.map((galleryImg, index) => (
                    <div key={galleryImg?.g_img_372x210} className={slideStyle}>
                        <Image
                            src={isMobile ? galleryImg?.g_img_372x210 : galleryImg?.g_img_760x428}
                            src2x={galleryImg?.g_img_760x428}
                            src3x={galleryImg?.g_img_1500}
                            alt={`${offer.name} - zdjęcie ${index + 1}`}
                            className={slideImgWrapperStyle}
                            imageStyle={offerBoxImgStyle}
                            width="375px"
                            height={isMobile ? "211px" : "246px"}
                            loading="lazy"
                            fetchPriority="low"
                            onClick={handleSlideClick}
                        />
                    </div>
                ))}
            </SimpleSlider>
        </div>
    );
};

const getOfferBoxBadge = (offer: IOfferBoxOffer) => {
    if (hasOfferNoContributionPromotion(offer)) {
        return (
            <Badge className={topBadgeStyle} variant="label_highlight">
                <Text as="span" variant="info_txt_2" className={specialPriceWrapperStyle}>
                    <SpecialPriceIcon className={specialPriceIconWrapperStyle} size="1.4" />
                    <strong>Bez wkładu własnego</strong>
                </Text>
            </Badge>
        );
    }

    if (hasOfferActivePromotions(offer)) {
        return (
            <Badge className={topBadgeStyle} variant="label_info">
                <Text as="span" variant="info_txt_2">
                    % <strong>Promocje</strong>
                </Text>
            </Badge>
        );
    }

    if (isOfferPreSale(offer)) {
        return (
            <Badge className={topBadgeStyle} variant="label_info">
                <Text as="span" variant="info_txt_2">
                    <strong>Przedsprzedaż</strong>
                </Text>
            </Badge>
        );
    }

    return null;
};

const offerBoxTopSectionStyle = css({
    display: "block",
    position: "relative",
    _hover: {
        color: "inherit",
        textDecoration: "none"
    },
    _focus: {
        color: "inherit",
        textDecoration: "none"
    }
});

const topBadgeStyle = css({
    position: "absolute",
    top: 1.5,
    left: 1.5
});

const specialPriceWrapperStyle = css({
    display: "flex",
    alignItems: "center",
    gap: 1
});

const specialPriceIconWrapperStyle = css({
    width: "1.2rem",
    height: "1.2rem"
});

const infoIconStyle = css({
    position: "absolute",
    bottom: 1.5,
    right: 1.5,
    zIndex: 5,
    cursor: "pointer"
});

const sliderStyle = css({
    overflow: "hidden",
    borderTopLeftRadius: 2,
    borderTopRightRadius: 2,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0
});

const slideStyle = css({
    position: "relative",
    height: "20.4rem",
    md: {
        height: "24.6rem"
    }
});

const slideImgWrapperStyle = css({
    display: "block",
    cursor: "pointer",
    objectFit: "cover",
    height: "100%",
    width: "100%"
});
